import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LabelList } from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { StoreInfoCells, StoreInfoHead } from "./StoreInfoCells";
import { ExportButton, TotalsRow } from "./ReportTotals";
import { HISTORY_REPORTS, STORE_COLS } from "./ReportHistory";
import { ChevronDown, ChevronRight, Loader2, Upload, Target } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/verizon-sales`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const fmtDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

export const PU40Chart = ({ daily }) => {
  if (!daily?.length) return <p className="text-sm text-slate-500 py-6 text-center">No daily sales yet this month.</p>;
  const data = daily.map((d) => ({ ...d, day: fmtDay(d.date) }));
  return (
    <div className="h-72" data-testid="pu40-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="day" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 11 }} label={{ value: "Tablets | FWA", angle: -90, position: "insideLeft", fontSize: 11 }} />
          <YAxis yAxisId="right" orientation="right" allowDecimals={false} tick={{ fontSize: 11 }} label={{ value: "Handsets", angle: 90, position: "insideRight", fontSize: 11 }} />
          <Tooltip />
          <Legend verticalAlign="top" />
          <Bar yAxisId="left" dataKey="tablets" name="Tablets" stackId="a" fill="#2dd4bf"><LabelList dataKey="tablets" position="inside" fontSize={10} /></Bar>
          <Bar yAxisId="left" dataKey="fwa" name="FWA" stackId="a" fill="#a78bfa"><LabelList dataKey="fwa" position="top" fontSize={10} /></Bar>
          <Line yAxisId="right" type="monotone" dataKey="handsets" name="Handsets" stroke="#dc2626" strokeWidth={2} dot={{ r: 2 }} connectNulls>
            <LabelList dataKey="handsets" position="top" fontSize={10} fill="#dc2626" />
          </Line>
        </ComposedChart>
      </ResponsiveContainer>
      <p className="text-[11px] text-slate-400 mt-1">Handsets come from each DLAR's daily numbers; tablets and FWA are the day-to-day change between DLAR uploads.</p>
    </div>
  );
};

const Uploader = ({ onDone }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/pu40/upload`, fd, auth());
      toast.success(`Loaded ${r.data.rows} stores (${r.data.matched} matched to a Store ID)`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };
  return (
    <label className="inline-flex" data-testid="pu40-upload">
      <input type="file" accept=".csv" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid="pu40-upload-input" />
      <span className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-teal text-white text-sm font-medium cursor-pointer hover:bg-teal/90 transition-colors">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Upload PU40 CSV
      </span>
    </label>
  );
};

const Row = ({ r, admin }) => {
  const [open, setOpen] = useState(false);
  const lift = r.lift_pct;
  return (
    <>
      <tr onClick={() => setOpen(!open)} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`pu40-row-${r.door_tsp}`}>
        <td className="py-2 px-3">{open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}</td>
        {admin && <StoreInfoCells r={r} />}
        <td className="py-2 px-3 font-medium text-navy">{r.store_id || "—"}</td>
        <td className="py-2 px-3 text-slate-600">{r.door_tsp}</td>
        <td className="py-2 px-3">{r.under_40}</td>
        <td className="py-2 px-3 text-right">{r.baseline_acts ?? "—"}</td>
        <td className="py-2 px-3 text-right">{r.mtd_pace ?? "—"}</td>
        <td className={`py-2 px-3 text-right font-semibold ${lift == null ? "" : lift >= 0 ? "text-emerald-600" : "text-red-600"}`}>{lift == null ? "—" : `${lift}%`}</td>
      </tr>
      {open && <tr><td colSpan={admin ? 9 : 7} className="bg-white px-3 py-4"><PU40Chart daily={r.daily} /></td></tr>}
    </>
  );
};

export const PU40Report = ({ mode = "admin", canUpload = false }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => axios.get(`${API}/${mode}/pu40`, auth()).then((r) => setD(r.data)).catch(() => setD({ rows: [] })), [mode]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;
  if (mode === "dealer" && !d.rows.length) return null;
  return (
    <Card className="border-slate-200" data-testid="pu40-report">
      <CardContent className="p-4 lg:p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-heading font-semibold text-navy text-lg flex items-center gap-2"><Target className="w-5 h-5 text-teal" />Project Under 40</h3>
            <p className="text-sm text-slate-500">{d.month ? `Verizon MA Reporting for ${d.month} · updated ${new Date(d.uploaded_at).toLocaleString()}` : "No PU40 report uploaded yet."} Click a store to see its daily chart.</p>
          </div>
          <div className="flex gap-2 flex-wrap">{canUpload && <Uploader onDone={load} />}<ExportButton title={`Project Under 40 ${d.month || ""}`} cols={[...STORE_COLS(mode === "admin"), ...HISTORY_REPORTS.pu40.cols]} rows={d.rows} testId="pu40-export-btn" /></div>
        </div>
        {d.rows.length > 0 && (
          <div className="overflow-hidden">
            <table className="vz-compact w-full">
              <thead><tr className="border-b border-slate-200 text-left text-slate-500">
                <th className="w-8" />{mode === "admin" && <StoreInfoHead />}<th className="py-2 px-3 font-medium">Store ID</th><th className="py-2 px-3 font-medium">TSP ID</th>
                <th className="py-2 px-3 font-medium">Under 40</th><th className="py-2 px-3 font-medium text-right">Baseline Trended Acts</th>
                <th className="py-2 px-3 font-medium text-right">MTD Pace</th><th className="py-2 px-3 font-medium text-right">Lift over Baseline</th>
              </tr></thead>
              <tbody>{d.rows.map((r) => <Row key={r.door_tsp} r={r} admin={mode === "admin"} />)}</tbody>
              <TotalsRow rows={d.rows} lead={mode === "admin" ? 6 : 4} label={`Total (${d.rows.length} stores)`} cols={HISTORY_REPORTS.pu40.cols.slice(1)} />
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
